"use client";

import React from "react";
import Link from "@/components/core/localized-link";
import { decode } from "html-entities";
import { t } from "@lingui/core/macro";
import { isLocaleCode, localizePath } from "@/utils/localize-path";

const mapSeoUrl: { [key: string]: string } = {
  it: "offerz-ch-it",
  en: "offerz-ch-en",
  fr: "offerz-ch-fr",
  de: "offerz-ch",
};

export interface FooterLinkProps {
  href: string;
  text: string;
}

const FooterLinks = ({ lang, links }: { lang: string; links: FooterLinkProps[] }) => {
  const locale = isLocaleCode(lang) ? lang : "de";

  const footerItems = [
    {
      title: t`Information`,
      items: links,
    },
    {
      title: t`Customer Service`,
      items: [
        {
          href: "https://help.offerz.ch/",
          text: t`Contact Us`,
        },
        {
          href: localizePath("/sitemap", locale),
          text: t`Site Map`,
        },
      ],
    },
    {
      title: t`Extras`,
      items: [
        {
          href: localizePath("/brands", locale),
          text: t`Brands`,
        },
        {
          href: localizePath(`/category/${mapSeoUrl[locale]}`, locale),
          text: t`Gift Certificates`,
        },
        {
          href: localizePath("/special", locale),
          text: t`Specials`,
        },
      ],
    },
    {
      title: t`My Account`,
      items: [
        {
          href: localizePath("/account", locale),
          text: t`My Account`,
        },
        {
          href: localizePath("/account/voucherwallet", locale),
          text: t`Voucherwallet`,
        },
        {
          href: localizePath("/account/orders", locale),
          text: t`Order History`,
        },
        {
          href: localizePath("/account/wishlist", locale),
          text: t`Wish List`,
        },
        {
          href: localizePath("/account/newsletter", locale),
          text: t`Newsletter`,
        },
      ],
    },
  ];

  return (
    <>
      {footerItems.map((section) => (
        <div key={section.title} className="w-full md:w-1/4">
          <div className="hidden md:block">
            <h5 className="mb-[25px] text-lg leading-[1.1] text-white lg:text-xl">
              {section.title}
            </h5>
            <ul className="text-[#aaa]">
              {section.items.map((link) => (
                <li key={link.text} className="px-0 pt-[2px]">
                  <Link
                    prefetch={false}
                    href={link.href}
                    data-testid={`footer-${link.text.trim().toLowerCase().replace(" ", "-")}-link`}
                    className="block p-0 transition-colors duration-300 hover:text-primary"
                  >
                    <span className="text-[15px]">{decode(link.text)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <details className="group border-b border-[#ffffff40] bg-transparent text-lg font-medium text-white md:hidden">
            <summary
              data-testid={`footer-${section.title.trim().toLowerCase().replace(" ", "-")}-accordion-btn`}
              className="flex cursor-pointer list-none items-center justify-between py-[15px]"
            >
              <span>{section.title}</span>
              <span className="transition-transform duration-300 group-open:rotate-180">
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </summary>
            <div className="mb-4 max-h-0 overflow-hidden transition-[max-height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-open:max-h-[1000px]">
              <ul className="text-white">
                {section.items.map((link) => (
                  <li key={link.text} className="px-0 pt-[2px]">
                    <Link
                      prefetch={false}
                      href={link.href}
                      data-testid={`footer-${link.text.trim().toLowerCase().replace(" ", "-")}-mobile-btn`}
                      className="block flex h-[40px] items-center p-0 transition-colors duration-300 hover:text-primary"
                    >
                      <span className="text-[17px]">{decode(link.text)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>
      ))}
    </>
  );
};

export default React.memo(FooterLinks);
